/**
 * ScheduleStep - Step para escolher entre usar agenda existente ou criar nova.
 *
 * O WizardShell expande este step em N x M instancias (locais x profissionais),
 * passando filterPlaceId e filterProfessionalId via stepProps.
 * Cada instancia configura exatamente 1 combo place+professional.
 *
 * - Se ja existe schedule: oferece "usar existente" (adicionar servico) ou "criar nova"
 * - Se nao existe: mostra config de criacao de agenda (dias/turnos)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, FormControlLabel, Checkbox, FormGroup, RadioGroup, Radio,
  TextField, Paper, Alert, Tooltip, IconButton,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  CalendarMonth as CalendarIcon,
  Update as UpdateIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { isTempId } from '../../../hooks/useEntityTree';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Segunda-feira', short: 'Seg' },
  { key: 'tuesday', label: 'Terca-feira', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta-feira', short: 'Qua' },
  { key: 'thursday', label: 'Quinta-feira', short: 'Qui' },
  { key: 'friday', label: 'Sexta-feira', short: 'Sex' },
  { key: 'saturday', label: 'Sabado', short: 'Sab' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
];

const DEFAULT_SINGLE = { start: '09:00', end: '17:00' };
const DEFAULT_MORNING = { start: '08:30', end: '12:00' };
const DEFAULT_AFTERNOON = { start: '13:30', end: '18:00' };

export default function ScheduleStep({
  entityTree,
  existingSchedules = [],
  stepProps = {},
  onValidChange,
}) {
  const { tree } = entityTree;
  const { filterProfessionalId, filterPlaceId } = stepProps;
  // choices[comboKey] = 'existing_{scheduleId}' | 'new'
  const [choices, setChoices] = useState({});
  // scheduleConfigs[comboKey] = { monday: { enabled, singleShift, single, morning, afternoon }, ... }
  const [scheduleConfigs, setScheduleConfigs] = useState({});

  // Quando filterPlaceId e filterProfessionalId sao fornecidos, temos exatamente 1 combo
  const combo = useMemo(() => {
    const place = filterPlaceId
      ? tree.places.find(p => p.id === filterPlaceId)
      : tree.places.find(p => p.selected);
    const prof = filterProfessionalId
      ? tree.professionals.find(p => p.id === filterProfessionalId)
      : tree.professionals.find(p => p.selected);

    if (!place || !prof) return null;

    const key = `${place.id}_${prof.id}`;
    const canHaveExisting = !isTempId(place.id) && !isTempId(prof.id);
    const matchingSchedules = canHaveExisting
      ? existingSchedules.filter(s =>
          s.professionalId === prof.id && s.placeId === place.id && s.isActive !== false
        )
      : [];
    return { key, place, prof, matchingSchedules };
  }, [tree.places, tree.professionals, filterPlaceId, filterProfessionalId, existingSchedules]);

  // Inicializar choices e configs para o combo
  useEffect(() => {
    if (!combo) return;

    if (!choices[combo.key]) {
      if (combo.matchingSchedules.length > 0) {
        setChoices(prev => ({ ...prev, [combo.key]: `existing_${combo.matchingSchedules[0].id}` }));
      } else {
        setChoices(prev => ({ ...prev, [combo.key]: 'new' }));
      }
    }
    if (!scheduleConfigs[combo.key]) {
      setScheduleConfigs(prev => ({
        ...prev,
        [combo.key]: DAYS_OF_WEEK.reduce((acc, day) => {
          acc[day.key] = {
            enabled: false,
            singleShift: true,
            single: { ...DEFAULT_SINGLE },
            morning: { ...DEFAULT_MORNING },
            afternoon: { ...DEFAULT_AFTERNOON },
          };
          return acc;
        }, {}),
      }));
    }
  }, [combo]);

  // Sempre valido (step opcional)
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  // Sync com entityTree quando choices ou configs mudam
  const syncToTree = useCallback((currentChoices, currentConfigs) => {
    if (!combo) return;

    const choice = currentChoices[combo.key];

    if (choice && choice.startsWith('existing_')) {
      const scheduleId = choice.replace('existing_', '');
      const schedule = combo.matchingSchedules.find(s => s.id === scheduleId);
      entityTree.addSchedule(combo.place.id, combo.prof.id, {
        name: schedule?.name || `Agenda existente`,
        existingScheduleId: scheduleId,
        isExisting: true,
        shifts: [],
      });
    } else if (choice === 'new') {
      const dayConfigs = currentConfigs[combo.key];
      if (!dayConfigs) return;

      const enabledDays = DAYS_OF_WEEK.filter(d => dayConfigs[d.key]?.enabled);
      if (enabledDays.length === 0) return;

      const services = entityTree.getServicesForProfessionalAtPlace(combo.place.id, combo.prof.id);
      const serviceIds = services.map(s => s.id);

      const groups = {};
      for (const day of enabledDays) {
        const dc = dayConfigs[day.key];
        if (dc.singleShift) {
          const gKey = `single_${dc.single.start}_${dc.single.end}`;
          if (!groups[gKey]) groups[gKey] = { type: 'single', startTime: dc.single.start, endTime: dc.single.end, days: [] };
          groups[gKey].days.push(day.key);
        } else {
          const mKey = `morning_${dc.morning.start}_${dc.morning.end}`;
          if (!groups[mKey]) groups[mKey] = { type: 'morning', startTime: dc.morning.start, endTime: dc.morning.end, days: [] };
          groups[mKey].days.push(day.key);
          const aKey = `afternoon_${dc.afternoon.start}_${dc.afternoon.end}`;
          if (!groups[aKey]) groups[aKey] = { type: 'afternoon', startTime: dc.afternoon.start, endTime: dc.afternoon.end, days: [] };
          groups[aKey].days.push(day.key);
        }
      }

      const shifts = Object.values(groups).map(group => {
        const dayLabels = group.days.map(dk => DAYS_OF_WEEK.find(d => d.key === dk)?.short || dk).join('/');
        const suffix = group.type === 'morning' ? ' - Manha' : group.type === 'afternoon' ? ' - Tarde' : '';
        return {
          name: `${dayLabels}${suffix} (${group.startTime}-${group.endTime})`,
          startTime: group.startTime,
          endTime: group.endTime,
          days: group.days,
          serviceIds,
        };
      });

      entityTree.addSchedule(combo.place.id, combo.prof.id, {
        name: `Agenda de ${combo.prof.name} em ${combo.place.name}`,
        isExisting: false,
        shifts,
      });
    }
  }, [combo, entityTree]);

  // Day config handlers
  const toggleDay = (comboKey, dayKey) => {
    setScheduleConfigs(prev => {
      const next = {
        ...prev,
        [comboKey]: {
          ...prev[comboKey],
          [dayKey]: {
            ...prev[comboKey][dayKey],
            enabled: !prev[comboKey][dayKey].enabled,
          },
        },
      };
      setTimeout(() => syncToTree(choices, next), 100);
      return next;
    });
  };

  const setShiftMode = (comboKey, dayKey, singleShift) => {
    setScheduleConfigs(prev => {
      const next = {
        ...prev,
        [comboKey]: {
          ...prev[comboKey],
          [dayKey]: { ...prev[comboKey][dayKey], singleShift },
        },
      };
      setTimeout(() => syncToTree(choices, next), 100);
      return next;
    });
  };

  const updateTime = (comboKey, dayKey, timeType, field, value) => {
    setScheduleConfigs(prev => {
      const next = {
        ...prev,
        [comboKey]: {
          ...prev[comboKey],
          [dayKey]: {
            ...prev[comboKey][dayKey],
            [timeType]: {
              ...prev[comboKey][dayKey][timeType],
              [field]: value,
            },
          },
        },
      };
      setTimeout(() => syncToTree(choices, next), 100);
      return next;
    });
  };

  const handleChoiceChange = (comboKey, value) => {
    const newChoices = { ...choices, [comboKey]: value };
    setChoices(newChoices);
    setTimeout(() => syncToTree(newChoices, scheduleConfigs), 100);
  };

  if (!combo) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="info">
          Selecione profissionais e locais nos passos anteriores para configurar agendas.
        </Alert>
      </Box>
    );
  }

  const currentChoice = choices[combo.key] || null;
  const currentConfig = scheduleConfigs[combo.key] || null;
  const hasExisting = combo.matchingSchedules.length > 0;

  // Render day config UI
  const renderDayConfig = (comboKey, dayConfigs) => (
    <FormGroup>
      {DAYS_OF_WEEK.map(day => {
        const dc = dayConfigs?.[day.key];
        if (!dc) return null;
        return (
          <Paper
            key={day.key}
            variant="outlined"
            sx={{ mb: 1.5, p: 2, bgcolor: dc.enabled ? 'action.selected' : 'transparent' }}
          >
            <FormControlLabel
              control={<Checkbox checked={dc.enabled} onChange={() => toggleDay(comboKey, day.key)} />}
              label={<Typography fontWeight={dc.enabled ? 'bold' : 'normal'}>{day.label}</Typography>}
            />
            {dc.enabled && (
              <Box sx={{ mt: 2, ml: 4 }}>
                <RadioGroup
                  row
                  value={dc.singleShift ? 'single' : 'double'}
                  onChange={(e) => setShiftMode(comboKey, day.key, e.target.value === 'single')}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <span>Turno unico</span>
                        <Tooltip title="Turno unico: sem intervalo de almoco">
                          <IconButton size="small" sx={{ ml: 0.5 }}><HelpIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                  <FormControlLabel value="double" control={<Radio size="small" />} label="Dois turnos" />
                </RadioGroup>
                {dc.singleShift ? (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <TextField label="Inicio" type="time" size="small" value={dc.single.start}
                      onChange={(e) => updateTime(comboKey, day.key, 'single', 'start', e.target.value)}
                      InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                    <TextField label="Fim" type="time" size="small" value={dc.single.end}
                      onChange={(e) => updateTime(comboKey, day.key, 'single', 'end', e.target.value)}
                      InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Turno da manha</Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Inicio" type="time" size="small" value={dc.morning.start}
                          onChange={(e) => updateTime(comboKey, day.key, 'morning', 'start', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                        <TextField label="Fim" type="time" size="small" value={dc.morning.end}
                          onChange={(e) => updateTime(comboKey, day.key, 'morning', 'end', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Turno da tarde</Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Inicio" type="time" size="small" value={dc.afternoon.start}
                          onChange={(e) => updateTime(comboKey, day.key, 'afternoon', 'start', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                        <TextField label="Fim" type="time" size="small" value={dc.afternoon.end}
                          onChange={(e) => updateTime(comboKey, day.key, 'afternoon', 'end', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        );
      })}
    </FormGroup>
  );

  // Render existing schedule summary
  const renderExistingScheduleSummary = (schedule) => (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CalendarIcon color="info" fontSize="small" />
        <Typography variant="subtitle2">{schedule.name}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        O(s) novo(s) servico(s) serao adicionados a todos os turnos ativos desta agenda.
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          Configure a agenda de <strong>{combo.prof.name}</strong> em <strong>{combo.place.name}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Escolha os dias da semana e turnos de atendimento. Você poderá fazer ajustes adicionais depois conforme necessário.
        </Typography>
      </Box>

      {hasExisting ? (
        // Profissional ja tem agenda(s) neste local
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Este profissional já possui agenda(s) neste local. Escolha usar uma existente ou criar uma nova.
          </Alert>

          <RadioGroup
            value={currentChoice || ''}
            onChange={(e) => handleChoiceChange(combo.key, e.target.value)}
          >
            {/* Opcoes de agendas existentes */}
            {combo.matchingSchedules.map(schedule => (
              <Box key={schedule.id} sx={{ mb: 2 }}>
                <FormControlLabel
                  value={`existing_${schedule.id}`}
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UpdateIcon color="info" fontSize="small" />
                      <span>Usar agenda existente: <strong>{schedule.name}</strong></span>
                    </Box>
                  }
                />
                {currentChoice === `existing_${schedule.id}` && (
                  <Box sx={{ ml: 4, mt: 1 }}>
                    {renderExistingScheduleSummary(schedule)}
                  </Box>
                )}
              </Box>
            ))}

            {/* Opcao de criar nova */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                value="new"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon color="success" fontSize="small" />
                    <span>Criar nova agenda</span>
                  </Box>
                }
              />
              {currentChoice === 'new' && (
                <Box sx={{ ml: 4, mt: 2 }}>
                  {renderDayConfig(combo.key, currentConfig)}
                </Box>
              )}
            </Box>
          </RadioGroup>
        </Box>
      ) : (
        // Nao existe agenda - mostrar criacao direto
        <Box>
          {renderDayConfig(combo.key, currentConfig)}
        </Box>
      )}
    </Box>
  );
}
